import React, { useEffect, useState } from 'react';
import {
  FormControl,
  FormLabel,
  GridItem,
  Box,
  Grid,
  Button,
  Textarea,
  NumberInput,
  NumberInputField,
  Heading,
  Flex,
  Spacer,
  IconButton,
  Input,
  HStack,
  useColorModeValue
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Select as ChakraSelect } from '@chakra-ui/react';
import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import { useNewKBLeadForm } from '../context/NewKBLeadFormContext';

const NewKBEventForm = () => {
  // Use the form context for state management
  const { 
    formData, 
    handleFormChange, 
    availableEventTypes,
    errors
  } = useNewKBLeadForm();

  // Initialize events from form data or start with an empty one
  const events = formData.events || [];
  
  // Log initial values for debugging
  useEffect(() => {
    console.log('[NewKBEventForm] Initial Event Form Values:', { 
      events, 
      eventTypes: availableEventTypes,
      typeValues: events.map(event => event.type)
    });
  }, []);
  
  // Validate event types when availableEventTypes changes
  useEffect(() => {
    if (availableEventTypes?.length > 0 && events?.length > 0) {
      // Check if any event types need to be updated
      let needsUpdate = false;
      const updatedEvents = events.map(event => {
        // If event.type is an object with _id, extract the _id
        if (typeof event.type === 'object' && event.type._id) {
          needsUpdate = true;
          return { ...event, type: event.type._id };
        }
        return event;
      });
      
      if (needsUpdate) {
        console.log('[NewKBEventForm] Updating event types to use IDs:', updatedEvents);
        // Update events directly through the context
        handleFormChange('events', updatedEvents);
      }
    }
  }, [availableEventTypes]);
  
  // Debug when events change
  useEffect(() => {
    console.log('[NewKBEventForm] Events changed:', { 
      events,
      eventTypes: availableEventTypes,
      formDataRef: formData.events === events
    });
  }, [events]);

  // Handle event changes
  const handleEventChange = (index, field, value) => {
    console.log(`Updating event[${index}].${field} to:`, value);
    
    const updatedEvents = [...events];
    updatedEvents[index] = {
      ...updatedEvents[index],
      [field]: value
    };
    
    handleFormChange('events', updatedEvents);
  };

  // Add a new event
  const addEvent = () => {
    const newEvent = { type: '', guestCount: 0 };
    handleFormChange('events', [...events, newEvent]);
  };

  // Remove an event
  const removeEvent = (index) => {
    const updatedEvents = [...events];
    updatedEvents.splice(index, 1);
    handleFormChange('events', updatedEvents);
  };

  // Get border color based on form state
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box mb={6}>
      <Flex mb={4}>
        <Heading size="md">Event Details</Heading>
        <Spacer />
        <IconButton
          icon={<AddIcon />}
          size="sm"
          colorScheme="blue"
          onClick={addEvent}
          aria-label="Add Event"
        />
      </Flex>

      {events.length === 0 ? (
        <Box p={4} borderWidth={1} borderRadius="md" borderColor={borderColor}>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            variant="outline"
            onClick={addEvent}
            w="100%"
          >
            Add Event
          </Button>
        </Box>
      ) : (
        <>
          {events.map((event, index) => (
        <GridItem key={index} colSpan={2}>
          <Box p={4} borderWidth={1} borderRadius="md" mb={4}>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>Event Type</FormLabel>
                  <ChakraSelect
                    value={event.type || ''}
                    onChange={(e) => {
                      console.log('[NewKBEventForm] Event Type Selected:', {
                        value: e.target.value,
                        currentType: event.type,
                        currentEvents: [...events],
                        index
                      });
                      handleEventChange(index, 'type', e.target.value);
                    }}
                  >
                    <option value="">Select Event Type</option>
                    {availableEventTypes && availableEventTypes.length > 0 ? (
                      availableEventTypes.map(type => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    )))
                    : (
                      <option disabled>No event types available</option>
                    )}
                  </ChakraSelect>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Guest Count</FormLabel>
                  <NumberInput min={0} value={event.guestCount || 0} onChange={(valueString) => {
                    const value = Number(valueString);
                    console.log('Guest Count Changed:', value);
                    handleEventChange(index, 'guestCount', value);
                  }}>
                    <NumberInputField
                      placeholder="Enter guest count"
                    />
                  </NumberInput>
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Event Time</FormLabel>
                  
                  {/* Custom date-time inputs that work better than DateRangePicker */}
                  <HStack spacing={4}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Start Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={event.startTime && !isNaN(new Date(event.startTime).getTime()) ? 
                          new Date(event.startTime).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          try {
                            const dateValue = new Date(e.target.value);
                            if (!isNaN(dateValue.getTime())) {
                              handleEventChange(index, 'startTime', dateValue.toISOString());
                              console.log('Start time changed to:', dateValue.toISOString());
                            }
                          } catch (err) {
                            console.error('Invalid start date value:', err);
                          }
                        }}
                      />
                    </FormControl>
                    
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">End Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={event.endTime && !isNaN(new Date(event.endTime).getTime()) ? 
                          new Date(event.endTime).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          try {
                            const dateValue = new Date(e.target.value);
                            if (!isNaN(dateValue.getTime())) {
                              handleEventChange(index, 'endTime', dateValue.toISOString());
                              console.log('End time changed to:', dateValue.toISOString());
                            }
                          } catch (err) {
                            console.error('Invalid end date value:', err);
                          }
                        }}
                      />
                    </FormControl>
                  </HStack>
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    value={event.notes || ''}
                    onChange={(e) => handleEventChange(index, 'notes', e.target.value)}
                    placeholder="Enter event notes"
                  />
                </FormControl>
              </GridItem>
            </Grid>

            {events.length > 1 && (
              <Button
                mt={2}
                size="sm"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                onClick={() => removeEvent(index)}
              >
                Remove Event
              </Button>
            )}
          </Box>
        </GridItem>
      ))}
        </>
      )}  
    </Box>
  );
};

export default NewKBEventForm;
